import {
  IEntityTableProps,
  IEntityDetailsProps,
  IEntityDetailsState,
  IEntityTableState,
  IEntity,
} from "packable-components";

import { SnackbarComponentVariant } from "packable-components";

import {
  IUserAppState,
  IClientEntity,
  ILocation,
  LocationEntity,
  DimensionUnit,
  WeightUnit,
  PaymentStatus,
} from "packable-common";

export interface IOrderTableProps extends IEntityTableProps {
  updateOrderURL: string;
  deleteOrderURL: string;
  isAdmin: boolean;
  userAppState: IUserAppState;
  clientEntity: IClientEntity;
}

export interface IOrderTableState extends IEntityTableState {}

export interface IOrderPageProps {
  displayMessage: (variant: SnackbarComponentVariant, message: string) => void;
  setDisplaySpinner: (flag: boolean) => void;
  userAppState: IUserAppState;
  clientEntity: IClientEntity;
  notificationCount: number;
  onUserAppStateUpdate: (userAppState: IUserAppState) => void;
  windowWidth: number;
  windowHeight: number;
  [x: string | number | symbol]: unknown;
}

export interface IOrderProps extends IEntityDetailsProps {
  updateOrderURL: string;
  deleteOrderURL: string;
  isAdmin: boolean;
  windowWidth: number;
  windowHeight: number;
  closeButtonLabel?: string;
  closeButtonTooltip?: string;
  cancelButtonLabel?: string;
  cancelButtonTooltip?: string;
  submitButtonLabel?: string;
  submitButtonTooltip?: string;
  orderPaymentButtonLabel?: string;
  orderPaymentButtonTooltip?: string;
  deleteButtonLabel?: string;
  deleteButtonTooltip?: string;
  payButtonLabel?: string;
  payButtonTooltip?: string;
}

export interface IOrderState extends IEntityDetailsState {
  orderActions: Array<OrderItemAction>;
  showOrderPayment: boolean;
  showDeleteOrderWarning: boolean;
  windowWidth: number;
  windowHeight: number;
}

export enum OrderStatus {
  DRAFT = "Draft",
  EXPIRED = "Expired",
  DELETED = "Deleted",
  CANCELLED = "Cancelled",
  STORAGE_PROCESSING = "Storage Processing",
  STORAGE_SHIPPING = "Shipping to Storage",
  STORAGE = "Storage",
  HOME_PROCESSING = "Home Processing",
  HOME_SHIPPING = "Shipping to Home",
  COMPLETED = "Completed",
}

export enum OrderType {
  STORAGE = "Storage",
  STORAGE_EXTENSION = "Storage Extension",
  HOME = "Home",
}

export enum ShipmentType {
  STORAGE = "Storage",
  HOME = "Home",
}

export enum ShippingStatus {
  DRAFT = "Draft",
  CANCEL_REQUESTED = "Cancellation Requested",
  CANCELLED = "Cancelled",
  REQUESTED = "Requested",
  ACCEPTED = "Accepted",
  REJECTED = "Rejected",
  LABELS_SHIPPED = "Labels Shipped",
  LABELS_DELIVERED = "Labels Delivered",
  PICKUP_REQUESTED = "Pickup Requested",
  PICKUP_SCHEDULED = "Pickup Scheduled",
  PICKUP_CANCELLED = "Pickup Cancelled",
  PICKUP_COMPLETED = "Pickup Completed",
  PICKUP_REJECTED = "Pickup Rejected",
  DELIVERED = "Delivered",
}

export enum StorageStatus {
  DRAFT = "Draft",
  ACTIVE = "Active",
  EXPIRED = "Expired",
  COMPLETED = "Completed",
}

export interface IShipmentItem extends IEntity {
  orderId: string;
  itemId: string;
  type: ShipmentType;
  status: ShippingStatus;
  trackingNumber: string;
  notes: string | null;
  usePickupHour: boolean;
  requestedPickup: Date | null;
  scheduledPickup: Date | null;
  actualPickup: Date | null;
  requestedDelivery: Date | null;
  scheduledDelivery: Date | null;
  actualDelivery: Date | null;
}

export interface IStorageItem extends IEntity {
  orderId: string;
  itemId: string;
  locationId: string;
  checkInDate: Date | null;
  checkOutDate: Date | null;
  status: StorageStatus;
  trackingNumber: string;
  notes: string | null;
}

export interface IOrderItem extends IEntity {
  orderId: string;
  itemId: string;
  itemLabel: string;
  itemLength: number;
  itemWidth: number;
  itemHeight: number;
  itemWeight: number;
  storageEndDate: Date;
  shipmentItem: IShipmentItem;
  storageItem: IStorageItem | null;
}

export interface IOrder extends IEntity {
  inventoryId: string;
  status: OrderStatus;
  paymentStatus: PaymentStatus;
  type: OrderType;
  orderNumber: string;
  entityId: string;
  entityName: string;
  userFirstName: string;
  userLastName: string;
  userPhoneNumber: string | undefined;
  userEmailAddress: string;
  dimensionUnit: DimensionUnit;
  weightUnit: WeightUnit;
  fromLocation: ILocation;
  orderItems: Array<IOrderItem>;
  createdBy: string;
  createdByName: string;
  createdDate: Date;
  lastModifiedBy: string;
  lastModifiedByName: string;
  lastModifiedDate: Date;
}

export type OrderItemStatus = {
  status: ShippingStatus;
  count: number;
};

export class ShipmentItemEntity implements IShipmentItem {
  id: string = "";
  orderId: string = "";
  itemId: string = "";
  type: ShipmentType = ShipmentType.STORAGE;
  status: ShippingStatus = ShippingStatus.DRAFT;
  trackingNumber: string = "";
  notes: string | null = null;
  usePickupHour: boolean = false;
  requestedPickup: Date | null = null;
  scheduledPickup: Date | null = null;
  actualPickup: Date | null = null;
  requestedDelivery: Date | null = null;
  scheduledDelivery: Date | null = null;
  actualDelivery: Date | null = null;
}

export class StorageItemEntity implements IStorageItem {
  id: string = "";
  orderId: string = "";
  itemId: string = "";
  locationId: string = "";
  checkInDate: Date | null = null;
  checkOutDate: Date | null = null;
  status: StorageStatus = StorageStatus.DRAFT;
  trackingNumber: string = "";
  notes: string | null = null;
}

export class OrderItemEntity implements IOrderItem {
  id: string = "";
  orderId: string = "";
  itemId: string = "";
  itemLabel: string = "";
  itemLength: number = 0;
  itemWidth: number = 0;
  itemHeight: number = 0;
  itemWeight: number = 0;
  storageEndDate: Date = new Date();
  shipmentItem: IShipmentItem = new ShipmentItemEntity();
  storageItem: IStorageItem = new StorageItemEntity();
}

export class OrderEntity implements IOrder {
  id: string = "";
  inventoryId: string = "";
  status: OrderStatus = OrderStatus.DRAFT;
  paymentStatus: PaymentStatus = PaymentStatus.UNKNOWN;
  type: OrderType = OrderType.STORAGE;
  orderNumber: string = "";
  entityId: string = "";
  entityName: string = "";
  userFirstName: string = "";
  userLastName: string = "";
  userPhoneNumber: string | undefined;
  userEmailAddress: string = "";
  dimensionUnit: DimensionUnit = DimensionUnit.INCH;
  weightUnit: WeightUnit = WeightUnit.POUND;
  fromLocation: ILocation = new LocationEntity();
  orderItems: Array<IOrderItem> = [];
  createdBy: string = "";
  createdByName: string = "";
  createdDate: Date = new Date();
  lastModifiedBy: string = "";
  lastModifiedByName: string = "";
  lastModifiedDate: Date = new Date();
}

export type OrderHeaderProps = {
  order: IOrder | undefined;
  styles?: any;
};

export type OrderItemProps = {
  orderItem: IOrderItem | undefined;
  orderItemAction: OrderItemAction;
  onOrderItemActionUpdate: (orderItemAction: OrderItemAction) => void;
  multipleItems?: boolean;
  isAdmin: boolean;
  dimensionUnit: DimensionUnit;
  weigthUnit: WeightUnit;
  styles?: any;
};

export type OrderStatusProps = {
  status: ShippingStatus;
  isAdmin: boolean;
  label?: string;
  count?: number;
  dispalyZeroCount?: boolean;
  displayChipForZeroCount?: boolean;
  disabled?: boolean;
  styles?: any;
};

export type OrderItemAction = {
  orderItemId: string;
  shipmentItemId: string;
  storageItemId: string;
  itemId: string;
  itemLabel: string;
  shippingStatus: ShippingStatus;
  storageStatus: StorageStatus;
  trackingNumber: string | null;
  note: string | null;
  issueRefund: boolean;
  usePickupHour: boolean;
  actionDate: Date | null;
  useTheSameDateAndTime: boolean;
};

export class OrderItemActionEntity implements OrderItemAction {
  orderItemId: string = "";
  shipmentItemId: string = "";
  storageItemId: string = "";
  itemId: string = "";
  itemLabel: string = "";
  shippingStatus: ShippingStatus = ShippingStatus.DRAFT;
  storageStatus = StorageStatus.DRAFT;
  trackingNumber: string | null = null;
  note: string | null = null;
  issueRefund: boolean = true;
  usePickupHour: boolean = false;
  actionDate: Date | null = null;
  useTheSameDateAndTime: boolean = false;
}

export type OrderActionsProps = {
  shipmentType?: ShipmentType;
  orderItemAction: OrderItemAction;
  onOrderItemActionUpdate: (orderItemAction: OrderItemAction) => void;
  multipleItems?: boolean;
  styles?: any;
};

export type OrderUpdateRequest = {
  orderId: string;
  orderItemActions: Array<OrderItemAction>;
};

export class OrderUpdateRequestEntity implements OrderUpdateRequest {
  orderId: string = "";
  orderItemActions: Array<OrderItemAction> = [];
}

export type OrderUserCancelActionsProps = {
  originalStatus: ShippingStatus;
  itemLabel: string;
  orderItemAction: OrderItemAction;
  onOrderItemActionUpdate: (orderItemAction: OrderItemAction) => void;
  disabled?: boolean;
  styles?: any;
};

export type OrderUserSchedulePickupActionsProps = {
  originalStatus: ShippingStatus;
  itemLabel: string;
  orderItemAction: OrderItemAction;
  multipleItems?: boolean;
  onOrderItemActionUpdate: (orderItemAction: OrderItemAction) => void;
  styles?: any;
};

export type ShippingStatusCount = {
  type: ShipmentType;
  status: ShippingStatus;
  count: number;
};

export type StorageStatusCount = {
  status: StorageStatus;
  count: number;
};

export type OrderStatusSummary = {
  shippingStatusCounts: Array<ShippingStatusCount>;
  storageStatusCounts: Array<StorageStatusCount>;
};

export type OrderStatusSummaryComponentProps = {
  displayMessage: (variant: SnackbarComponentVariant, message: string) => void;
  setDisplaySpinner: (flag: boolean) => void;
  windowWidth: number;
  windowHeight: number;
  styles?: any;
};

export class OrderStatusSummaryEntity implements OrderStatusSummary {
  shippingStatusCounts: Array<ShippingStatusCount> = [];
  storageStatusCounts: Array<StorageStatusCount> = [];
}

export interface IOrderSummary extends IEntity {
  status: OrderStatus;
  type: OrderType;
  orderNumber: string;
  shippingStatusCounts: Array<ShippingStatusCount>;
  minStorageEndDate: Date;
  maxStorageEndDate: Date;
  userFirstName: string;
  userLastName: string;
  userPhoneNumber: string;
  userEmailAddress: string | null;
  fromPostalCode: string;
  createdByName: string;
  createdDate: Date;
  lastModifiedByName: string;
  lastModifiedDate: string;
}
